import * as z from "zod";
import { pagination, uuidV7, version, versionQuery } from "../index";
import type { Dataset, Fields, ValuesSchema } from "./dataset";
import { type AnyDataset, type DatasetKey, datasetKeys, datasets } from "./datasets";

// The records of the reference data (50-api/reference.md): the same four operations for every dataset. A record
// says which dataset it belongs to, so each input and each response is checked against the fields of that one.

/** One schema per dataset, as the options of a union discriminated by `dataset`. */
function perDataset<V extends z.ZodObject>(build: (dataset: Dataset) => z.ZodObject): [V, ...V[]] {
  return Object.values(datasets).map(build) as unknown as [V, ...V[]];
}

/** The id of a record: its natural key where the dataset has one, else a uuid. */
const idOf = (dataset: Dataset, uuid: z.ZodType<string> = z.uuid()) => dataset.code ?? uuid;

type Changes<F extends Fields> = z.ZodObject<{ [N in keyof F]: z.ZodOptional<F[N]["schema"]> }>;

type RecordOf<D> =
  D extends Dataset<infer K, infer F>
    ? z.ZodObject<{
        dataset: z.ZodLiteral<K>;
        id: z.ZodString;
        version: z.ZodNumber;
        updatedAt: z.ZodISODateTime;
        values: ValuesSchema<F>;
      }>
    : never;

type CreateOf<D> =
  D extends Dataset<infer K, infer F>
    ? z.ZodObject<{ dataset: z.ZodLiteral<K>; id: z.ZodString; values: ValuesSchema<F> }>
    : never;

type ChangeOf<D> =
  D extends Dataset<infer K, infer F>
    ? z.ZodObject<{ dataset: z.ZodLiteral<K>; version: z.ZodNumber; values: Changes<F> }>
    : never;

type UpdateOf<D> =
  D extends Dataset<infer K, infer F>
    ? z.ZodObject<{ dataset: z.ZodLiteral<K>; id: z.ZodString; version: z.ZodNumber; values: Changes<F> }>
    : never;

const recordOf = (dataset: Dataset) =>
  z.object({
    dataset: z.literal(dataset.key),
    id: z.string(),
    version: z.number().int(),
    updatedAt: z.iso.datetime(),
    values: dataset.values,
  });

/** The response schema of each dataset: its presenter is defined on it. */
export const referenceRecordDtos = Object.fromEntries(
  Object.values(datasets).map((dataset) => [dataset.key, recordOf(dataset)]),
) as unknown as { [K in DatasetKey]: RecordOf<(typeof datasets)[K]> };

export const referenceRecordDto = z
  .discriminatedUnion(
    "dataset",
    perDataset<RecordOf<AnyDataset>>((dataset) => referenceRecordDtos[dataset.key as DatasetKey]),
  )
  .meta({ id: "ReferenceRecord" });
export type ReferenceRecord = z.output<typeof referenceRecordDto>;

/** The records of a dataset, in the order the dataset is read in (its code or its name). */
export const listReferenceRecordsInput = z.object({
  dataset: z.enum(datasetKeys),
  /** Part of a text field of the record, or of its code. */
  q: z.string().trim().min(1).max(100).optional(),
  ...pagination({ defaultSize: 100, maxSize: 500 }),
});
export type ListReferenceRecordsInput = z.input<typeof listReferenceRecordsInput>;

/** A new record, with the id from the client (ADR-046): a uuid v7, or the code of a dataset with a natural key. */
export const createReferenceRecordInput = z.discriminatedUnion(
  "dataset",
  perDataset<CreateOf<AnyDataset>>((dataset) =>
    z.object({ dataset: z.literal(dataset.key), id: idOf(dataset, uuidV7), values: dataset.values }),
  ),
);
export type CreateReferenceRecordInput = z.input<typeof createReferenceRecordInput>;

export const referenceRecordParams = z.object({ id: z.string().min(1).max(40) });

/** Body of a change: a field left out stays as it is, `null` empties it (50-api/README §3). */
export const updateReferenceRecordBody = z.discriminatedUnion(
  "dataset",
  perDataset<ChangeOf<AnyDataset>>((dataset) =>
    z.object({ dataset: z.literal(dataset.key), version, values: dataset.values.partial() }),
  ),
);

export const updateReferenceRecordInput = z.discriminatedUnion(
  "dataset",
  perDataset<UpdateOf<AnyDataset>>((dataset) =>
    z.object({ dataset: z.literal(dataset.key), id: idOf(dataset), version, values: dataset.values.partial() }),
  ),
);
export type UpdateReferenceRecordInput = z.input<typeof updateReferenceRecordInput>;

/** Query of a deletion: the dataset of the record and the version the client read. */
export const deleteReferenceRecordQuery = versionQuery.extend({ dataset: z.enum(datasetKeys) });

export const deleteReferenceRecordInput = referenceRecordParams.extend(deleteReferenceRecordQuery.shape);
export type DeleteReferenceRecordInput = z.input<typeof deleteReferenceRecordInput>;

export const deletedReferenceRecordDto = z.object({ dataset: z.enum(datasetKeys), id: z.string() });
